import React from 'react';
import { useQueries } from 'react-query';
import { Box, Typography } from '@mui/material';
import { usePropertyId } from '../../../../common/hooks/usePropertyId';
import { useRoomsApi } from '../../../../common/hooks/useRoomsApi';
import { useRoomsData } from '../../../../common/hooks/useRoomsData';
import { usePlansData } from '../../../../common/hooks/useRatePlansData';
import { useWebsiteContentData } from '../../../../common/hooks/useWebsiteContentData';
import { defaultStaleTime, withMultipleQueryData } from '../../../../common/withQueryData';
import { normalizeAccommodationsContent, mergeAccommodationsWithRooms } from '../Accommodations';
import RoomOffersTable from './RoomOffersTable';

/**
 * Fetches rooms, plans, and accommodations and renders RoomOffersTable.
 * Only mounted when room offers exist, so data is fetched only when necessary.
 */
const RoomOffers = ({ roomOffers, booking, onSelectOffer }) => {
  const propertyId = usePropertyId();

  const { queryResults: roomsQueryResults } = useRoomsData(propertyId);
  const { queryResults: plansQueryResults } = usePlansData(propertyId);
  const { queryResults: accommodationsQueryResults } = useWebsiteContentData(
    propertyId,
    'accommodations'
  );

  return (
    <RoomOffersTableWithData
      queryResults={{
        rooms: roomsQueryResults,
        plans: plansQueryResults,
        accommodations: accommodationsQueryResults,
      }}
      propertyId={propertyId}
      roomOffers={roomOffers}
      booking={booking}
      onSelectOffer={onSelectOffer}
    />
  );
};

const getNightsLabel = (nights) => {
  if (nights === 1) return '1 noc';
  if (nights >= 2 && nights <= 4) return `${nights} noce`;
  return `${nights} nocy`;
};

const RoomOffersTableContent = ({ queryResults, propertyId, roomOffers, booking, onSelectOffer }) => {
  const rooms = queryResults.rooms?.data ?? [];
  const ratePlans = queryResults.plans?.data ?? [];
  const accommodationsData = queryResults.accommodations?.data;
  const { specs } = normalizeAccommodationsContent(accommodationsData);
  const mergedRoomItems = mergeAccommodationsWithRooms(specs, rooms) ?? [];
  const { getPictures } = useRoomsApi();

  const uniquePictureRoomIds = React.useMemo(
    () => [...new Set((roomOffers || []).map((ro) => ro.roomId).filter((id) => id != null && id !== ''))],
    [roomOffers]
  );

  const pictureQueryResults = useQueries(
    uniquePictureRoomIds.map((roomId) => ({
      queryKey: ['roomPictures', { propertyId, roomId }],
      queryFn: () => getPictures(propertyId, roomId),
      staleTime: defaultStaleTime,
      enabled: !!propertyId && roomId != null && roomId !== '',
    }))
  );

  const roomMainPhotoByRoomId = React.useMemo(() => {
    const map = {};
    uniquePictureRoomIds.forEach((roomId, i) => {
      const album = pictureQueryResults[i]?.data;
      const url = album?.pictureUrls?.[0];
      if (url) map[String(roomId)] = url;
    });
    return map;
  }, [uniquePictureRoomIds, pictureQueryResults]);

  const { checkin, checkout, adults } = booking;
  const lengthOfStay =
    checkin && checkout
      ? Math.ceil((new Date(checkout) - new Date(checkin)) / (24 * 60 * 60 * 1000))
      : 0;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Dostępne oferty
        </Typography>
        {lengthOfStay > 0 && (
          <Typography variant="body2" color="text.secondary">
            Pobyt: {getNightsLabel(lengthOfStay)}
          </Typography>
        )}
      </Box>
      <RoomOffersTable
        roomOffers={roomOffers}
        rooms={rooms}
        ratePlans={ratePlans}
        mergedRoomItems={mergedRoomItems}
        roomMainPhotoByRoomId={roomMainPhotoByRoomId}
        adults={adults}
        checkin={checkin}
        checkout={checkout}
        onSelectOffer={onSelectOffer}
      />
    </Box>
  );
};

const RoomOffersTableWithData = withMultipleQueryData(RoomOffersTableContent);

export default RoomOffers;
